import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check } from '@phosphor-icons/react';

export const Newsletter = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    // Prevent duplicate script injection
    if (containerRef.current.querySelector('script[data-uid="1239e73aa7"]')) return;
    const script = document.createElement('script');
    script.src = 'https://worldsoccerparty.kit.com/1239e73aa7/index.js';
    script.async = true;
    script.setAttribute('data-uid', '1239e73aa7');
    containerRef.current.appendChild(script);
  }, []);

  return (
    <section id="newsletter" className="py-24 sm:py-32 bg-[#0E2E53]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl tracking-tight uppercase text-white mb-4">
            Stay in the Game
          </h2>
          <p className="text-lg text-[#A0B3C6] max-w-2xl mx-auto">
            Get exclusive updates about viewing events, special announcements and early access to tickets and giveaways.
          </p>
        </motion.div>

        {/* (Msg 781) Kit newsletter + Heart & Stroke Charity side by side.
            Consolidates the old "Become a Partner" section into the newsletter
            band so the landing page stays clean — one row instead of two. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="h-full"
          >
            <div className="bg-gradient-to-br from-[#0E2E53] to-[#050B14] border-2 border-[#00F0A9] p-8 shadow-[0_0_30px_rgba(0,240,169,0.15)] relative overflow-hidden h-full">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00F0A9] to-transparent animate-pulse"></div>
              {/* Kit.com Embed */}
              <div ref={containerRef} data-testid="newsletter-form" className="kit-embed-wrapper" />
              <div className="flex items-start gap-2 mt-6 text-xs text-[#A0B3C6]">
                <Check size={16} className="text-[#00F0A9] mt-0.5 flex-shrink-0" />
                <p>By subscribing, you'll get soccer 2026 viewing party updates, events near you in 16 hosted cities, and fan coverage all summer long.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white border border-gray-200 rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow h-full"
            data-testid="heart-stroke-block"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="h-16 sm:h-20 flex items-center flex-shrink-0">
                <img
                  src="/partners/heart-stroke.jpg"
                  alt="Heart & Stroke Foundation"
                  className="h-full w-auto object-contain"
                  data-testid="heart-stroke-logo"
                  loading="lazy"
                />
              </div>
              <div className="min-w-0">
                <h3 className="font-['Bebas_Neue'] text-3xl uppercase text-gray-900 leading-tight">Charity Partner</h3>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#E2231A]">In Support Of</p>
              </div>
            </div>
            <h4 className="text-2xl font-extrabold text-[#E2231A] mb-1">Heart &amp; Stroke Foundation</h4>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-5">Promoting health · Saving lives · Enhancing recovery</p>
            <p className="text-gray-700 leading-relaxed mb-6">
              Heart &amp; Stroke is dedicated to fighting heart disease and stroke. With your support, we lead lifesaving research, drive system change, save lives, and improve the lives of every person in Canada.
            </p>
            <div className="space-y-2.5 mb-6">
              {[
                'Funding world-class heart & stroke research',
                'CPR & defibrillator training in every community',
                'Advocating for healthier policies across Canada',
              ].map(item => (
                <div key={item} className="flex items-start gap-2.5">
                  <Check size={18} weight="bold" className="text-[#E2231A] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">{item}</p>
                </div>
              ))}
            </div>
            <a
              href="https://www.heartandstroke.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#E2231A] hover:bg-[#B91C13] text-white px-6 py-3 font-bold uppercase tracking-wider transition-colors inline-block w-full text-center rounded-lg"
              data-testid="heart-stroke-link"
            >
              Learn More at heartandstroke.ca
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
